import { useIntl } from 'react-intl';
import { generatePath } from 'react-router';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  ReportRequest_InputType_CBCT_IOS_Superimposition,
  ReportRequest_InputType_StudioImplant,
  ReportRequest_InputType_StudioOrtho,
} from '@/shared/api/protocol-ts/model/dto_report_request_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useAppSelector } from '@/shared/hooks';
import {
  getImageSrc,
  getSegmentronReportLink,
  getStudyIDsByType,
  trackEvent,
} from '@/shared/lib';

import { assetsModel } from '@/entities/assets';
import { billingModel } from '@/entities/billing';
import { useDiagnocoinFundingByStudyType } from '@/entities/composed';
import { patientModel } from '@/entities/patient';
import {
  getReportPath,
  superimpositionOrientationLabels,
  useNavigateToReport,
} from '@/entities/reports';

import { useNewReport } from '@/features/addNewReport';
import { useDownloadReportPDF } from '@/features/downloadReportPDF';
import { useFDA3DGPDownloadRestriction } from '@/features/fdaRestrictions';
import { useRemoveReport } from '@/features/removeReport';
import { useRemoveStudy } from '@/features/removeStudy';
import { StructuredReportsAndStudiesType } from '@/features/reportCards';

import { reportCardTitle } from '../config';
import {
  getRelevantStudy,
  getSegmentronPreview,
  hasReportPhoto,
  isLegacySegmentronReport,
  isSecondaryStudy,
  isStudyOfType,
} from '../lib';

// TODO: This hook should be decomposed. It has too much cohesion. structuredReportsAndStudies is a pain in the ass.
export const useReportCard = (
  structuredReportsAndStudies: StructuredReportsAndStudiesType,
) => {
  const { formatMessage } = useIntl();

  const { CanRequestReport } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(
      structuredReportsAndStudies.report?.PatientID ?? '',
    ),
  );

  const {
    requestIOXRayGPReport,
    requestPanoGPReport,
    requestCBCTGPReport,
    requestCBCTSegmentationReport,
    requestStudioOrthoReport,
    requestStudioImplantReport,
    requestCBCTIOSSuperimpositionReport,
    requestCBCTOrthoReport,
    requestIOSSegmentationReport,
  } = useNewReport();
  const removeReport = useRemoveReport();
  const removeStudy = useRemoveStudy();

  const reportType = structuredReportsAndStudies.reportType;

  const report = structuredReportsAndStudies?.report;
  const studies = structuredReportsAndStudies?.studies;
  const date = structuredReportsAndStudies?.date;

  const relevantStudy = getRelevantStudy(studies);
  const relevantStudyType = relevantStudy?.Type;

  const patientID = report?.PatientID;

  const reportID = report?.ID;
  const reportStatus = report?.Status;
  const isReportGenerationCompleted = reportStatus?.Completed;
  const isReportSigned = report?.Signature?.Signed;
  const isReportFailed = reportStatus?.Failed;

  const isSegmentronReport = [
    ReportType.ReportType_CBCT_Segmentation,
    ReportType.ReportType_CBCT_IOS_Superimposition,
    ReportType.ReportType_IOS_Segmentation,
    ReportType.ReportType_StudioImplant,
    ReportType.ReportType_StudioOrtho,
  ].includes(reportType);
  const isIntraoralScanReport =
    reportType === ReportType.ReportType_IOS_Segmentation;
  const isCBCTOrthoPDFReport =
    reportType === ReportType.ReportType_CBCT_OrthoSlides;

  const isReportOrderAvailable = useAppSelector(
    billingModel.selectors.selectIsReportOrderAvailableByStudyType(
      relevantStudyType ?? StudyType.StudyType_InvalidValue,
    ),
  );

  const reorderFunding = useDiagnocoinFundingByStudyType(relevantStudyType);

  const shouldRestrictDownload = useFDA3DGPDownloadRestriction(reportID ?? '');

  const isDownloadRestrictedReport = [
    ReportType.ReportType_CBCT_Segmentation,
    ReportType.ReportType_CBCT_IOS_Superimposition,
    ReportType.ReportType_StudioImplant,
  ].includes(reportType);

  const shouldHideDownload =
    isDownloadRestrictedReport || shouldRestrictDownload;

  const CBCTOrthoPDFUrl = useAppSelector(
    assetsModel.selectors.selectCBCTOrthoPDFUrl(report?.ID ?? ''),
  );

  const navigateToReport = useNavigateToReport();

  const { downloadReportPDF, downloading } = useDownloadReportPDF(
    reportID ?? '',
  );

  const downloadReportHandler = async () => {
    if (
      isCBCTOrthoPDFReport ||
      reportType === ReportType.ReportType_StudioOrtho
    ) {
      window.open(CBCTOrthoPDFUrl, '_blank', 'noopener,noreferrer');
    } else {
      await downloadReportPDF();
    }
  };

  const isPanowingsReport =
    relevantStudyType === StudyType.StudyType_PanoramicXRay &&
    report?.Specific.case === 'DataPanoBitewings';

  const isIOXRayOrPanowings =
    relevantStudyType === StudyType.StudyType_IntraoralXRay ||
    isPanowingsReport;

  const previewImageSrc = getImageSrc(report?.DisplayAssetID, 'preview');

  const cardPreviewSrc =
    isSegmentronReport && isLegacySegmentronReport(report)
      ? getSegmentronPreview(reportType)
      : previewImageSrc;

  const superimpositionOrientation =
    report?.Specific.case === 'DataCBCTIOSSuperimposition'
      ? report.Specific.value.Orientation
      : undefined;

  const baseReportTitle =
    superimpositionOrientation !== undefined
      ? `${formatMessage(reportCardTitle[reportType])} (${formatMessage(
          superimpositionOrientationLabels[superimpositionOrientation],
        )})`
      : formatMessage(reportCardTitle[reportType]);

  const reportTitle = hasReportPhoto(studies)
    ? `${baseReportTitle} + ${formatMessage({
        id: 'reportCard.reportWithPhoto',
        defaultMessage: 'Photo',
      })}`
    : baseReportTitle;

  const getReportUrl = () => {
    if (isCBCTOrthoPDFReport) {
      return CBCTOrthoPDFUrl;
    }

    if (isSegmentronReport) {
      return getSegmentronReportLink(reportID ?? '', reportType);
    }

    if (patientID && reportID) {
      const path = getReportPath(reportType);

      if (path) {
        return generatePath(path, { patientID, reportID });
      }
    }

    return null;
  };

  const trackReportClick = () => {
    trackEvent({
      eventName: 'PatientsPageReportWidgetClicked',
      properties: {
        report_id: reportID,
        report_type: reportType,
      },
    });
  };

  const handleCardClick = () => {
    if (isReportGenerationCompleted && !isReportFailed) {
      trackReportClick();

      navigateToReport({
        reportType,
        reportID: reportID ?? '',
        patientID: patientID ?? '',
        CBCTOrthoPDFUrl,
      });
    }
  };

  const handleMiddleClick = () => {
    if (isReportGenerationCompleted && !isReportFailed) {
      const url = getReportUrl();

      if (url) {
        trackReportClick();
        window.open(url, '_blank');
      }
    }
  };

  const reorderReport = () => {
    const CBCTStudyID = studies?.find(
      isStudyOfType(StudyType.StudyType_CBCT),
    )?.ID;
    const IOXRayStudyID = studies?.find(
      isStudyOfType(StudyType.StudyType_IntraoralXRay),
    )?.ID;
    const panoStudyID = studies?.find(
      isStudyOfType(StudyType.StudyType_PanoramicXRay),
    )?.ID;
    const IOSMeshesStudyID = studies?.find(
      isStudyOfType(StudyType.StudyType_IOS_Meshes),
    )?.ID;
    const dentalPhotoStudyIDs = getStudyIDsByType(
      studies ?? [],
      StudyType.StudyType_DentalPhoto,
    );

    if (CBCTStudyID) {
      if (reportType === ReportType.ReportType_CBCT_GP) {
        requestCBCTGPReport(CBCTStudyID);
      }

      if (reportType === ReportType.ReportType_CBCT_Segmentation) {
        requestCBCTSegmentationReport(CBCTStudyID);
      }

      if (reportType === ReportType.ReportType_CBCT_IOS_Superimposition) {
        requestCBCTIOSSuperimpositionReport(
          new ReportRequest_InputType_CBCT_IOS_Superimposition({
            CBCTStudyID,
            STLStudyID: IOSMeshesStudyID,
            DentalPhotoStudyIDs: dentalPhotoStudyIDs,
            Orientation: superimpositionOrientation,
          }),
        );
      }

      if (reportType === ReportType.ReportType_StudioImplant) {
        requestStudioImplantReport(
          new ReportRequest_InputType_StudioImplant({
            CBCTStudyID,
            IOSMeshesStudyID,
            DentalPhotoStudyIDs: dentalPhotoStudyIDs,
          }),
        );
      }

      if (reportType === ReportType.ReportType_CBCT_OrthoSlides) {
        requestCBCTOrthoReport(CBCTStudyID);
      }

      if (reportType === ReportType.ReportType_StudioOrtho) {
        const secondaryCBCTStudyID = studies?.find(
          isSecondaryStudy(CBCTStudyID, StudyType.StudyType_CBCT),
        )?.ID;

        requestStudioOrthoReport(
          new ReportRequest_InputType_StudioOrtho({
            CBCTStudyID,
            IOSMeshesStudyID,
            DentalPhotoStudyIDs: dentalPhotoStudyIDs,
            SecondaryCBCTStudyID: secondaryCBCTStudyID,
          }),
        );
      }
    }

    if (IOXRayStudyID && reportType === ReportType.ReportType_IOXRay_GP) {
      requestIOXRayGPReport(IOXRayStudyID);
    }

    if (panoStudyID && reportType === ReportType.ReportType_Pano_GP) {
      requestPanoGPReport(panoStudyID);
    }

    if (
      IOSMeshesStudyID &&
      reportType === ReportType.ReportType_IOS_Segmentation
    ) {
      requestIOSSegmentationReport(IOSMeshesStudyID);
    }
  };

  const removeReportHandler = async (options?: { onError?: () => void }) => {
    const response = await removeReport(
      structuredReportsAndStudies?.activeReportIDs,
      options,
    );

    // NOTE: A temporary solution until ReportType_IOS_Segmentation is fully implemented
    if (response && isIntraoralScanReport) {
      await removeStudy(relevantStudy?.ID ?? '');
    }

    return response;
  };

  return {
    isReportGenerationCompleted,
    isIOXRayOrPanowings,
    isPanowingsReport,
    isSegmentronReport,
    reportType,
    cardPreviewSrc,
    report,
    relevantStudy,
    date,
    isReportFailed,
    isReportSigned,
    isReportOrderAvailable,
    reorderFunding,
    isLoadingPDF: downloading,
    isCBCTOrthoPDFReport,
    CanRequestReport,
    isIntraoralScanReport,
    shouldHideDownload,
    reportTitle,
    handleCardClick,
    handleMiddleClick,
    reorderReport,
    downloadReportHandler,
    removeReportHandler,
    superimpositionOrientation,
  };
};
